import type { Lead } from "@/mocks/leadsData";

type Props = {
  deals?: Lead[];
  pipelineData?: Record<string, number[]>;
  dealMetaMap?: Record<number, any>;
};

export function PipelineStatsBar({ deals = [] }: Props) {
  // Compute metrics dynamically from actual database leads
  const parseValueNumber = (valStr: string | undefined): number => {
    if (!valStr) return 0;
    const num = parseFloat(valStr.replace(/[₹,]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const totalValue = deals.reduce((sum, d) => sum + parseValueNumber(d.value), 0);

  const wonDeals = deals.filter(
    (d) =>
      d.stage === "Booking Done" ||
      d.stage === "Closed Won" ||
      d.stage?.toLowerCase().includes("won") ||
      d.stage?.toLowerCase().includes("booking")
  );

  const activeDeals = deals.filter(
    (d) =>
      d.stage !== "Lost" &&
      d.stage !== "Closed Lost" &&
      !d.stage?.toLowerCase().includes("lost")
  );

  const avgDealSize = deals.length > 0 ? Math.round(totalValue / deals.length) : 0;
  const winRate = deals.length > 0 ? Math.round((wonDeals.length / deals.length) * 100) : 0;

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount.toLocaleString()}`;
  };

  const stats = [
    {
      icon: "ri-money-rupee-circle-line",
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
      label: "Total Pipeline Value",
      value: formatCurrency(totalValue),
      subtext: `${deals.length} total deals`,
    },
    {
      icon: "ri-check-double-line",
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
      label: "Deals Won",
      value: String(wonDeals.length),
      subtext: `${winRate}% conversion rate`,
    },
    {
      icon: "ri-funds-line",
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      label: "Avg Deal Size",
      value: formatCurrency(avgDealSize),
      subtext: `${activeDeals.length} active in pipeline`,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 shadow-sm hover:shadow transition-shadow"
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.iconBg}`}>
            <i className={`${stat.icon} text-xl ${stat.iconColor}`} />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 truncate">{stat.value}</p>
            <p className="text-xs font-medium text-gray-500">{stat.label}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{stat.subtext}</p>
          </div>
        </div>
      ))}
    </div>
  );
}